import React from 'react';
import { Linkedin, ArrowRight, MapPin, Check } from 'lucide-react';
import { FOUNDER } from '../mock';

const initials = FOUNDER.name
  .split(' ')
  .map((n) => n[0])
  .slice(0, 2)
  .join('');

const Founder = () => {
  return (
    <section id="founder" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-indigo-50/40 to-white p-8 md:p-12 lg:p-14">
          {/* Ambient background */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-16 -right-16 w-80 h-80 rounded-full bg-indigo-300/20 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-cyan-300/20 blur-3xl"
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: photo card */}
            <div className="lg:col-span-4">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 opacity-15 blur-2xl" />
                <div className="relative rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 overflow-hidden">
                  {FOUNDER.photo ? (
                    <img
                      src={FOUNDER.photo}
                      alt={FOUNDER.name}
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                      <span className="font-display text-[56px] font-semibold text-white tracking-tight">
                        {initials}
                      </span>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-[15px] font-semibold text-slate-900">
                      {FOUNDER.name}
                    </div>
                    <div className="text-[12.5px] text-slate-500 mt-0.5">
                      {FOUNDER.role}
                    </div>
                    <div className="mt-3 flex items-center gap-1.5 text-[12px] text-slate-500">
                      <MapPin size={12} className="text-indigo-500" />
                      {FOUNDER.location}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: content */}
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 uppercase tracking-[0.18em]">
                Meet the founder
              </div>
              <h2 className="mt-4 font-display text-[32px] md:text-[44px] leading-[1.05] font-semibold tracking-tight text-slate-900">
                Every engagement,{' '}
                <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
                  personally led.
                </span>
              </h2>

              <p className="mt-5 text-[15.5px] leading-relaxed text-slate-600">
                {FOUNDER.bio}
              </p>
              {FOUNDER.bioExtended && (
                <p className="mt-4 text-[15.5px] leading-relaxed text-slate-600">
                  {FOUNDER.bioExtended}
                </p>
              )}

              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FOUNDER.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-[13.5px] text-slate-700">
                    <Check size={15} className="mt-0.5 text-emerald-500 flex-none" strokeWidth={2.5} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <a
                  href={FOUNDER.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 font-medium text-[14px] hover:border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <Linkedin size={15} className="text-indigo-600" />
                  Connect on LinkedIn
                </a>
                <a
                  href="#contact"
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white font-medium text-[14px] hover:bg-slate-800 transition-colors"
                >
                  Book a call
                  <ArrowRight
                    size={15}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Founder;
